import axios, { AxiosError, AxiosInstance } from 'axios'

import {
  File as ClevverFile,
  Tag,
  User,
  Quota,
  Purchase,
  Pagination,
  Invoice,
  ViewMode,
  ApiKey,
  SecurityKey
} from '@clevver/types'

interface APIError {
  error: string,
  details?: string
  statusCode?: number
}

interface RequestConfig {
  params?: any
  headers?: Record<string, string>
  onProgress?: (percent: number, loaded: number, total: number) => void
}
interface ApiResponse<T> {
  data: T[],
  pagination?: Pagination,
  meta?: any
}

interface SingleApiResponse<T> {
  data?: T,
  error?: string
}

export interface GetFilesParams {
  page?: number
  tags?: Tag[],
  archived?: boolean
  viewMode?: ViewMode
}

type RequestMethod = 'get' | 'post' | 'patch' | 'put' | 'delete'

export interface ClientConfig {
  baseURL?: string
  token?: string | null
  onUnauthorized?: () => void
}

let authToken: string | null = null
let onUnauthorized: (() => void) | null = null

const http: AxiosInstance = axios.create({ baseURL: '/api' })

http.interceptors.request.use(config => {
  if (authToken) {
    config.headers = config.headers ?? {}
    config.headers['Authorization'] = `Bearer ${authToken}`
  }
  return config
})

http.interceptors.response.use(
  response => response,
  error => {
    const isAuthEndpoint = typeof error?.config?.url === 'string' && error.config.url.includes('/auth/')
    if (error?.response?.status === 401 && onUnauthorized && !isAuthEndpoint) {
      onUnauthorized()
    }
    return Promise.reject(error)
  }
)

export function configureClient(config: ClientConfig) {
  if (config.baseURL !== undefined) http.defaults.baseURL = config.baseURL
  if (config.token !== undefined) authToken = config.token
  if (config.onUnauthorized !== undefined) onUnauthorized = config.onUnauthorized
}

class ClevverClient {

  public static getInstance(): ClevverClient {
    return new ClevverClient()
  }

 /*
  * Get the current user
  *
  * @returns User
  */
  public async getUser(): Promise<SingleApiResponse<User>> {
    return this.request<SingleApiResponse<User>>('get', `/user.json`)
  }

  public async getQuota(): Promise<SingleApiResponse<Quota>> {
    return this.request<SingleApiResponse<Quota>>('get', `/user/quota.json`)
  }

  public async getInvoices(): Promise<ApiResponse<Invoice>> {
    let data = {}

    try {
      const response = await http.get(`/invoices.json`)
      return response.data
    } catch(error) {
      return {
        data: []
      }
    }
  }

  public async createPurchase(): Promise<SingleApiResponse<Purchase>> {
    return this.request<SingleApiResponse<Purchase>>('post', `/purchases.json`)
  }

  public async startPurchaseJob(purchaseId: number): Promise<SingleApiResponse<Purchase>> {
    return this.request<SingleApiResponse<Purchase>>('post', `/purchases/${purchaseId}/start.json`)
  }

  public async uploadDemoFile(emailAddress: string, file: File, onProgress?: Function): Promise<any> {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append("email_address", emailAddress)

    return this.request<any>('post', `/signup/upload.json`, {
      params: formData,
      headers: headers,
      onProgress: (percent, loaded, total) => {
        if (onProgress) {
          onProgress(percent, loaded, total)
        }
      }
    })
  }

  public async uploadFile(purchaseId: number, file: File, onProgress?: Function): Promise<SingleApiResponse<File>> {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }

    const formData = new FormData()
    formData.append('file', file)

    return this.request<SingleApiResponse<File>>('post', `/purchases/${purchaseId}/files.json`, {
      params: formData,
      headers: headers,
      onProgress: (percent, loaded, total) => {
        if (onProgress) {
          onProgress(percent, loaded, total)
        }
      }
    })
  }
  public async getFiles({ page = 1, tags = [], archived = false, viewMode = ViewMode.all}: GetFilesParams): Promise<ApiResponse<ClevverFile>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        filter: viewMode,
        archived: archived.toString()
      })

      if (tags.length > 0) {
        tags.forEach(tag => params.append('tags[]', tag))
      }

      const response = await http.get(`/files.json`, { params })
      return response.data
    } catch(error) {
      // TODO: Handle errors! Maybe we should abstract this and have a generic error handler?
      console.log("There was some kind of error fetching the files: ", error)

      return {
        data: [],
        pagination: {
          total_entries: 0,
          total_pages: 0,
          current_page: 0,
          per_page: 0
        }
      }
    }
  }

  public async getFile(id: string): Promise<SingleApiResponse<ClevverFile>> {
    try {
      const response = await http.get(`/files/${id}.json`)
      return response.data
    } catch(error) {
      return {
        error: "There was some kind of error fetching the file."
      }
    }
  }

  public async updateFile(id: string, params: any): Promise<SingleApiResponse<ClevverFile>> {
    try {
      const response = await http.patch(`/files/${id}.json`, params)
      return response.data
    } catch(error) {
      return {
        error: "There was some kind of error updating the file."
      }
    }
  }

  public async archiveFile(id: string): Promise<SingleApiResponse<ClevverFile>> {
    try {
      const response = await http.patch(`/files/${id}/archive.json`)
      return response.data
    } catch(error) {
      return {
        error: "A network error occurred while trying to archive the file."
      }
    }
  }

  public async unarchiveFile(id: string): Promise<SingleApiResponse<ClevverFile>> {
    try {
      const response = await http.patch(`/files/${id}/unarchive.json`)
      return response.data
    } catch(error) {
      return {
        error: "A network error occurred while trying to restore the file."
      }
    }
  }

  public async getTags(): Promise<ApiResponse<Tag>> {
    try {
      const response = await http.get(`/files/tags.json`)
      return response.data
    } catch(error) {
      return {
        data: []
      }
    }
  }

  public async changePassword(currentPassword: string, newPassword: string, passwordConfirmation: string): Promise<SingleApiResponse<any>> {
    try {
      const response = await http.patch(`/user/password.json`, {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: passwordConfirmation
      })
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while updating your password." }
    }
  }

  public async getApiKeys(): Promise<ApiResponse<ApiKey>> {
    try {
      const response = await http.get(`/api_keys.json`)
      return response.data
    } catch(error) {
      return { data: [] }
    }
  }

  public async createApiKey(name: string): Promise<SingleApiResponse<ApiKey>> {
    try {
      const response = await http.post(`/api_keys.json`, { name })
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while creating the API key." }
    }
  }

  public async updateApiKey(id: number, params: { name?: string, active?: boolean }): Promise<SingleApiResponse<ApiKey>> {
    try {
      const response = await http.patch(`/api_keys/${id}.json`, params)
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while updating the API key." }
    }
  }

  public async deleteApiKey(id: number): Promise<SingleApiResponse<any>> {
    try {
      const response = await http.delete(`/api_keys/${id}.json`)
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while deleting the API key." }
    }
  }

  public async getSecurityKeys(): Promise<ApiResponse<SecurityKey>> {
    try {
      const response = await http.get(`/passkeys.json`)
      return response.data
    } catch(error) {
      return { data: [] }
    }
  }

  public async getSecurityKeyOptions(): Promise<any> {
    const response = await http.post(`/passkeys/options.json`)
    return response.data
  }

  public async createSecurityKey(credential: any, nickname: string): Promise<SingleApiResponse<SecurityKey>> {
    try {
      const response = await http.post(`/passkeys.json`, { credential, nickname })
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while registering the security key." }
    }
  }

  public async renameSecurityKey(id: number, nickname: string): Promise<SingleApiResponse<SecurityKey>> {
    try {
      const response = await http.patch(`/passkeys/${id}.json`, { nickname })
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while renaming the security key." }
    }
  }

  public async deleteSecurityKey(id: number): Promise<SingleApiResponse<any>> {
    try {
      const response = await http.delete(`/passkeys/${id}.json`)
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }
      return { error: "A network error occurred while removing the security key." }
    }
  }

  public async createPaymentIntent(token: string, storage: number): Promise<SingleApiResponse<any>> {
    try {
      const response = await http.post(`/user/payment-intent.json`, {
        token,
        storage
      })
      return response.data
    } catch(error) {
      if (error.response) {
        return { error: error.response.data.error }
      }

      return {
        error: "A network error occurred while trying to archive the file."
      }
    }
  }

  private async request<T>(method: RequestMethod, url: string, config?: RequestConfig): Promise<T | APIError> {
    try {
      const requestOptions = {
        method,
        url,
        data: config?.params,
        headers: config?.headers,
        onUploadProgress: e => {
          let percent = e.loaded / e.total * 100 | 0

          if (config?.onProgress) {
            config?.onProgress(percent, e.loaded, e.total)
          }
        }
      }

      const response = await http(requestOptions)

      return response.data
    } catch(error) {
      const axiosError = error as AxiosError
      const statusCode = axiosError.response?.status
      const apiError: APIError = {
        error: `There was an error ${method === 'patch' ? 'updating' : 'fetching'} the resource.`,
        details: axiosError.message,
        statusCode
      }

      return apiError
    }
  }

  public async login(emailAddress: string, password: string): Promise<SingleApiResponse<{ token: string }>> {
    return this.authPost('/auth/login.json', { email_address: emailAddress, password })
  }

  public async requestMagicLink(emailAddress: string): Promise<SingleApiResponse<any>> {
    return this.authPost('/auth/magic_link.json', { email_address: emailAddress, mobile: true })
  }

  public async exchangeMagicLink(token: string): Promise<SingleApiResponse<{ token: string, password_set: boolean }>> {
    return this.authPost('/auth/magic_link_exchange.json', { token })
  }

  public async forgotPassword(emailAddress: string): Promise<SingleApiResponse<any>> {
    return this.authPost('/auth/forgot_password.json', { email_address: emailAddress, mobile: true })
  }

  public async resetPassword(token: string, password: string, passwordConfirmation: string): Promise<SingleApiResponse<{ token: string }>> {
    return this.authPost('/auth/reset_password.json', { token, password, password_confirmation: passwordConfirmation })
  }

  public async signupStart(emailAddress: string): Promise<SingleApiResponse<any>> {
    return this.authPost('/auth/signup.json', { email_address: emailAddress })
  }

  public async signupVerify(emailAddress: string, pin: string): Promise<SingleApiResponse<{ signup_token: string }>> {
    return this.authPost('/auth/signup/verify.json', { email_address: emailAddress, pin })
  }

  public async signupComplete(signupToken: string, fullName: string, password: string, passwordConfirmation: string): Promise<SingleApiResponse<{ token: string }>> {
    return this.authPost('/auth/signup/complete.json', {
      signup_token: signupToken, full_name: fullName,
      password, password_confirmation: passwordConfirmation, terms: "1"
    })
  }

  private async authPost<T>(url: string, payload: any): Promise<SingleApiResponse<T>> {
    try {
      const response = await http.post(url, payload)
      return { data: response.data?.data }
    } catch (error) {
      const axiosError = error as AxiosError<any>
      return { error: axiosError.response?.data?.error ?? "A network error occurred. Please try again." }
    }
  }

}

export const client = ClevverClient.getInstance()


